import { useMemo, useContext, lazy, Suspense } from "react"
import RouteError from "../base/route-error.js"
import PrivateRoute from "../base/private-route.js"
import { featuresFlatMap } from "utils"
import { AppContext } from "../base/app-context-provider.js"
import { Loader } from "widgets"

const ManageBankAccounts = lazy(() => import("./manage-bank-accounts.js"))
const ManageSuppliers = lazy(() => import("./manage-suppliers.js"))
const SupplierDetails = lazy(() => import("./supplier-details.js"))
const ManageDepositRequests = lazy(() => import("./manage-deposit-requests.js"))
const DepositRequestDetails = lazy(() => import("./deposit-request-details.js"))
const ManageBankAccountsList = lazy(
  () => import("./manage-bank-accounts-list.js"),
)
const ManageSupplierPayments = lazy(
  () => import("./manage-supplier-payments.js"),
)
const SupplierPayment = lazy(() => import("./supplier-payment.js"))
const SupplierLedger = lazy(() => import("../ledgers/supplier-ledger.js"))
const BankLedger = lazy(() => import("../ledgers/bank-ledger.js"))
const AgencyLedger = lazy(() => import("../ledgers/agency-ledger.js"))
const SalesLedger = lazy(() => import("../ledgers/sales-ledger.js"))
const PurchaseLedger = lazy(() => import("../ledgers/purchase-ledger.js"))
const ProfitAndLoss = lazy(() => import("../ledgers/profit-and-loss.js"))

export const useAccountsRoutes = () => {
  const appContext = useContext(AppContext)

  const features = useMemo(
    () => featuresFlatMap(appContext?.user?.featureDTOList)?.["accounts"] ?? [],
    [appContext?.user?.featureDTOList],
  )

  const accountsRoutes = useMemo(
    () =>
      _.compact([
        {
          path: "accounts",
          errorElement: <RouteError />,
          element: <PrivateRoute />,
          children: _.compact([
            features["search-bank-account"] && {
              path: "manage-bank-accounts",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageBankAccounts />
                </Suspense>
              ),
            },
            features["search-supplier"] && {
              path: "manage-suppliers",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageSuppliers />
                </Suspense>
              ),
            },
            features["add-supplier"] && {
              path: "add-supplier",
              element: (
                <Suspense fallback={<Loader />}>
                  <SupplierDetails mode="add" />
                </Suspense>
              ),
            },
            features["search-supplier-payments"] && {
              path: "manage-supplier-payments",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageSupplierPayments parentRoute="/accounts/manage-suppliers" />
                </Suspense>
              ),
            },
            features["supplier-ledger"] && {
              path: "supplier-ledger",
              element: (
                <Suspense fallback={<Loader />}>
                  <SupplierLedger />
                </Suspense>
              ),
            },
            features["add-supplier-payment"] && {
              path: "add-supplier-payment",
              element: (
                <Suspense fallback={<Loader />}>
                  <SupplierPayment />
                </Suspense>
              ),
            },
            features["modify-supplier"] && {
              path: "modify-supplier",
              element: (
                <Suspense fallback={<Loader />}>
                  <SupplierDetails mode="modify" />
                </Suspense>
              ),
            },
            features["search-agency-deposits"] && {
              path: "manage-deposit-requests",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageDepositRequests
                    title={t("deposit_requests.manage_agency_deposit")}
                    formHeading={t("deposit_requests.search_agency_deposits")}
                  />
                </Suspense>
              ),
            },
            features["search-my-deposits"] && {
              path: "my-deposits",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageDepositRequests
                    title={t("deposit_requests.manage_my_deposit")}
                    formHeading={t("deposit_requests.search_my_deposits")}
                    type="self"
                  />
                </Suspense>
              ),
            },
            (features["search-my-deposits"] || features["view-my-deposit"]) && {
              path: "view-my-deposit",
              element: (
                <Suspense fallback={<Loader />}>
                  <DepositRequestDetails
                    mode="view"
                    parentRoute={
                      appContext?.user?.officeDTO.officeLevel === 0
                        ? "/accounts/manage-deposit-requests"
                        : "/accounts/my-deposits"
                    }
                    title={
                      appContext?.user?.officeDTO.officeLevel === 0
                        ? t("deposit_requests.manage_agency_deposit")
                        : t("deposit_requests.manage_my_deposit")
                    }
                    formHeading={
                      appContext?.user?.officeDTO.officeLevel === 0
                        ? t("deposit_requests.view_agency_deposit")
                        : t("deposit_requests.view_my_deposit")
                    }
                  />
                </Suspense>
              ),
            },
            features["update-deposit-request"] && {
              path: "update-deposit",
              element: (
                <Suspense fallback={<Loader />}>
                  <DepositRequestDetails
                    mode="update"
                    parentRoute="/accounts/manage-deposit-requests"
                    title={t("deposit_requests.manage_agency_deposit")}
                    formHeading={t("deposit_requests.update_agency_deposit")}
                  />
                </Suspense>
              ),
            },
            features["update-deposit-request"] && {
              // For L1 agency to view online deposits
              path: "view-deposit",
              element: (
                <Suspense fallback={<Loader />}>
                  <DepositRequestDetails
                    mode="view"
                    parentRoute="/accounts/manage-deposit-requests"
                    title={t("deposit_requests.manage_agency_deposit")}
                    formHeading={t("deposit_requests.view_agency_deposit")}
                  />
                </Suspense>
              ),
            },
            features["bank-ledger"] && {
              path: "bank-ledger",
              element: (
                <Suspense fallback={<Loader />}>
                  <BankLedger />
                </Suspense>
              ),
            },
            features["add-my-deposit"] && {
              path: "add-deposit",
              element: (
                <Suspense fallback={<Loader />}>
                  <DepositRequestDetails
                    mode="add"
                    parentRoute="/accounts/my-deposits"
                    title={t("deposit_requests.manage_my_deposit")}
                    formHeading={t("deposit_requests.add_my_deposit")}
                  />
                </Suspense>
              ),
            },
            features["add-agency-deposit"] && {
              path: "add-agency-deposit",
              element: (
                <Suspense fallback={<Loader />}>
                  <DepositRequestDetails
                    mode="add"
                    formHeading={t("deposit_requests.add_agency_deposit")}
                    parentRoute="/accounts/manage-deposit-requests"
                    title={t("deposit_requests.manage_agency_deposit")}
                    type="agency"
                  />
                </Suspense>
              ),
            },
            features["agency-ledger"] && {
              path: "agency-ledger",
              element: (
                <Suspense fallback={<Loader />}>
                  <AgencyLedger
                    title="Agency Ledger"
                    formHeading="Search Agency Ledger"
                  />
                </Suspense>
              ),
            },
            features["my-ledger"] && {
              path: "my-ledger",
              element: (
                <Suspense fallback={<Loader />}>
                  <AgencyLedger
                    title="My Ledger"
                    formHeading="Search My Ledger"
                    mode="self"
                  />
                </Suspense>
              ),
            },
            features["purchase-ledger"] && {
              path: "purchase-ledger",
              element: (
                <Suspense fallback={<Loader />}>
                  <PurchaseLedger
                    title="Purchase Ledger"
                    formHeading="Search Purchase Ledger"
                  />
                </Suspense>
              ),
            },
            features["sales-ledger"] && {
              path: "sale-ledger",
              element: (
                <Suspense fallback={<Loader />}>
                  <SalesLedger
                    title="Sale Ledger"
                    formHeading="Search Sale Ledger"
                  />
                </Suspense>
              ),
            },
            features["profit-loss"] && {
              path: "profit-and-loss",
              element: (
                <Suspense fallback={<Loader />}>
                  <ProfitAndLoss />
                </Suspense>
              ),
            },
            features["bank-account-list"] && {
              path: "list-bank-accounts",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageBankAccountsList />
                </Suspense>
              ),
            },
          ]),
        },
      ]),
    [appContext?.user?.officeDTO.officeLevel, features],
  )
  return accountsRoutes
}
